/**
 * English: Build daily/periodic summary rows for a device from memory events + station rollups.
 * Thai: สร้างแถวสรุปรายวัน/รายงวดต่ออุปกรณ์จาก events และ rollups ระดับสถานี
 */
import type {
  DailySummaryItem,
  PeriodicSummaryItem,
} from '../../hooks/useMemorySummary';

export type MemoryEventRow = {
  event_id: string;
  content: string;
  role?: string | null;
  source?: string | null;
  mode?: string | null;
  salience?: number | null;
  occurred_at: string;
};

export function memoryMentionsDevice(
  summaryMd: string,
  deviceId: string,
  agentId?: string,
): boolean {
  const hay = summaryMd.toLowerCase();
  const needles = [deviceId, agentId]
    .map((s) => s?.trim())
    .filter((s): s is string => Boolean(s));
  return needles.some((n) => hay.includes(n.toLowerCase()));
}

export function filterSummariesForDevice<T extends { summary_md: string }>(
  items: T[],
  deviceId: string,
  agentId?: string,
): T[] {
  return items.filter((item) => memoryMentionsDevice(item.summary_md, deviceId, agentId));
}

export function rollupEventsToDaily(
  events: MemoryEventRow[],
  orgId: string,
  stationId: string,
): DailySummaryItem[] {
  const buckets = new Map<string, MemoryEventRow[]>();
  for (const event of events) {
    const day = event.occurred_at?.slice(0, 10);
    if (!day) continue;
    const list = buckets.get(day) ?? [];
    list.push(event);
    buckets.set(day, list);
  }

  return [...buckets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([summary_date, dayEvents]) => {
      const sorted = [...dayEvents].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));
      const summary_md = sorted
        .map(
          (e) =>
            `- **${e.source || 'event'}**${e.role ? ` (${e.role})` : ''}: ${(e.content || '').trim()}`,
        )
        .join('\n');
      return {
        org_id: orgId,
        station_id: stationId,
        summary_date,
        summary_md: summary_md || '- (empty)',
        source_turn_count: sorted.length,
        created_at: sorted[0].occurred_at,
        updated_at: sorted[sorted.length - 1].occurred_at,
      };
    });
}

function weekStartIso(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

export function rollupEventsToPeriodic(
  dailyItems: DailySummaryItem[],
  orgId: string,
  stationId: string,
): PeriodicSummaryItem[] {
  const weeks = new Map<string, DailySummaryItem[]>();
  for (const item of dailyItems) {
    const key = weekStartIso(item.summary_date);
    const list = weeks.get(key) ?? [];
    list.push(item);
    weeks.set(key, list);
  }

  return [...weeks.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([period_start_date, days]) => {
      const sorted = [...days].sort((a, b) => a.summary_date.localeCompare(b.summary_date));
      const period_end_date = sorted[sorted.length - 1].summary_date;
      const source_turn_count = sorted.reduce((n, d) => n + (d.source_turn_count || 0), 0);
      const summary_md = sorted.map((d) => `### ${d.summary_date}\n${d.summary_md}`).join('\n\n');
      return {
        org_id: orgId,
        station_id: stationId,
        period_type: 'week' as const,
        period_start_date,
        period_end_date,
        summary_md,
        source_turn_count,
        created_at: sorted[0].created_at,
        updated_at: sorted[sorted.length - 1].updated_at,
      };
    });
}

export function mergeDailySummaries(...groups: DailySummaryItem[][]): DailySummaryItem[] {
  const map = new Map<string, DailySummaryItem>();
  for (const items of groups) {
    for (const item of items) {
      const key = item.summary_date;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, { ...item });
        continue;
      }
      map.set(key, {
        ...existing,
        summary_md: `${existing.summary_md}\n\n---\n\n${item.summary_md}`,
        source_turn_count: existing.source_turn_count + item.source_turn_count,
        updated_at: item.updated_at > existing.updated_at ? item.updated_at : existing.updated_at,
      });
    }
  }
  return [...map.values()].sort((a, b) => a.summary_date.localeCompare(b.summary_date));
}

export function mergePeriodicSummaries(...groups: PeriodicSummaryItem[][]): PeriodicSummaryItem[] {
  const map = new Map<string, PeriodicSummaryItem>();
  for (const items of groups) {
    for (const item of items) {
      const key = `${item.period_type}:${item.period_start_date}`;
      const existing = map.get(key);
      if (!existing) {
        map.set(key, { ...item });
        continue;
      }
      map.set(key, {
        ...existing,
        period_end_date:
          item.period_end_date > existing.period_end_date
            ? item.period_end_date
            : existing.period_end_date,
        summary_md: `${existing.summary_md}\n\n---\n\n${item.summary_md}`,
        source_turn_count: existing.source_turn_count + item.source_turn_count,
        updated_at: item.updated_at > existing.updated_at ? item.updated_at : existing.updated_at,
      });
    }
  }
  return [...map.values()].sort((a, b) =>
    b.period_start_date.localeCompare(a.period_start_date),
  );
}
